import React, { useState } from "react";
import { Form, Input, Button, Progress } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const PasswordLoginForm = ({ onLogin, loading }) => {
  const [form] = Form.useForm();
  const [passwordStrength, setPasswordStrength] = useState(0);
  const { t } = useTranslation();

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[a-z]/.test(password)) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password) || /[^A-Za-z0-9]/.test(password)) strength += 25;
    return strength;
  };

  const getPasswordStrengthText = (strength) => {
    if (strength < 50) return t("login.passwordStrength.weak");
    if (strength < 75) return t("login.passwordStrength.medium");
    return t("login.passwordStrength.strong");
  };

  const getPasswordStrengthColor = (strength) => {
    if (strength < 50) return "#ff4d4f";
    if (strength < 75) return "#faad14";
    return "#52c41a";
  };

  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setPasswordStrength(calculatePasswordStrength(password));
  };

  const onFinish = async (values) => {
    await onLogin(values.username, values.password);
  };

  return (
    <Form
      form={form}
      name="login"
      onFinish={onFinish}
      autoComplete="off"
      size="large"
      layout="vertical"
    >
      <Form.Item
        name="username"
        label={t("login.username")}
        rules={[
          { required: true, message: t("login.usernameRequired") },
          { type: "email", message: t("login.emailFormatError") },
        ]}
      >
        <Input
          prefix={<UserOutlined />}
          placeholder={t("login.username")}
        />
      </Form.Item>

      <Form.Item
        name="password"
        label={t("login.password")}
        rules={[
          { required: true, message: t("login.passwordRequired") },
          { min: 8, max: 20, message: t("login.passwordLengthError") },
        ]}
      >
        <Input.Password
          prefix={<LockOutlined />}
          placeholder={t("login.password")}
          onChange={handlePasswordChange}
        />
      </Form.Item>

      {passwordStrength > 0 && (
        <div className="password-strength">
          <div
            style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}
          >
            {getPasswordStrengthText(passwordStrength)}
          </div>
          <Progress
            percent={passwordStrength}
            showInfo={false}
            strokeColor={getPasswordStrengthColor(passwordStrength)}
            size="small"
          />
        </div>
      )}

      <Form.Item shouldUpdate>
        {() => {
          const username = form.getFieldValue("username");
          const password = form.getFieldValue("password");
          const passwordValid =
            !!password && password.length >= 8 && password.length <= 20;
          const disabled = !username || !passwordValid;
          return (
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={disabled}
              style={{ width: "100%" }}
            >
              {t("login.loginButton")}
            </Button>
          );
        }}
      </Form.Item>
    </Form>
  );
};

export default PasswordLoginForm;
